import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { uploadFile, deleteFile, getSignedUrl } from '../../../lib/upload'

const FIELDS = `
  id, title, description, file_path, file_name, file_size,
  mime_type, category, member_id, uploaded_by, created_at,
  profiles!uploaded_by(display_name, avatar_url, member_color)
`

export const useDocuments = (filters = {}) => {
  const { household } = useAuth()
  const key = ['documents', household?.id, filters]

  return useQuery({
    queryKey: key,
    enabled: !!household?.id,
    queryFn: async () => {
      let q = supabase
        .from('documents')
        .select(FIELDS)
        .eq('household_id', household.id)
        .order('created_at', { ascending: false })

      if (filters.category && filters.category !== 'all') {
        q = q.eq('category', filters.category)
      }
      if (filters.memberId && filters.memberId !== 'all') {
        q = q.eq('member_id', filters.memberId)
      }

      const { data, error } = await q
      if (error) throw error
      return data ?? []
    },
  })
}

export const useStorageUsage = () => {
  const { household } = useAuth()

  return useQuery({
    queryKey: ['storage-usage', household?.id],
    enabled: !!household?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('household_storage_usage')
        .select('total_bytes')
        .eq('household_id', household.id)
        .maybeSingle()
      if (error) throw error
      return data?.total_bytes ?? 0
    },
  })
}

export const useDocumentMutations = () => {
  const { household, user } = useAuth()
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['documents', household?.id] })
    qc.invalidateQueries({ queryKey: ['storage-usage', household?.id] })
  }

  const upload = useMutation({
    mutationFn: async ({ file, title, description, category, memberId, onProgress }) => {
      const ext  = file.name.split('.').pop()
      const path = `${household.id}/${user.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`

      const { path: storedPath, fileSize, mimeType } = await uploadFile(
        file, 'household-documents', path, household.id,
        { compressionOpts: { maxWidthPx: 1920, maxHeightPx: 1920 } }
      )

      const { error } = await supabase.from('documents').insert({
        household_id: household.id,
        uploaded_by:  user.id,
        member_id:    memberId || null,
        title:        title || file.name,
        description:  description || null,
        file_path:    storedPath,
        file_name:    file.name,
        file_size:    fileSize,
        mime_type:    mimeType || file.type,
        category:     category || 'other',
      })
      if (error) {
        await deleteFile('household-documents', storedPath).catch(() => {})
        throw error
      }
    },
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: async ({ id, title, description, category, memberId }) => {
      const { error } = await supabase
        .from('documents')
        .update({ title, description, category, member_id: memberId || null })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: async ({ id, filePath }) => {
      await deleteFile('household-documents', filePath)
      const { error } = await supabase.from('documents').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const getUrl = async (filePath) => getSignedUrl('household-documents', filePath)

  return { upload, update, remove, getUrl }
}
